import type { ReactNode } from 'react'
import { ProctoringBlockerType, type DeviceRecoveryFailureReason } from 'proctoring-sdk/index'
import { DesktopDisconnectedIcon } from 'Components/common/Icons/DesktopDisconnectedIcon'
import { MicrophoneDisconnectedIcon } from 'Components/common/Icons/MicrophoneDisconnectedIcon'
import { WebcamDisconnectedIcon } from 'Components/common/Icons/WebcamDisconnectedIcon'
import { ScreenShareIcon } from 'Components/common/Icons/ScreenShareIcon'
import { CheckAgainIcon } from 'Components/common/Icons/CheckAgainIcon'
import {
    DEVICE_FAILURE_HINTS,
    SCREEN_SHARE_FAILURE_HINTS,
} from 'Components/Proctoring/proctoringPermissions.constants'

const CHECK_AGAIN_LABEL = 'Check Again'
const CHECK_AGAIN_ICON = <CheckAgainIcon />
const SCREEN_SHARE_ICON = <ScreenShareIcon />

type FailureHints = Partial<Record<DeviceRecoveryFailureReason, string>>

interface ProctoringBlockerContent {
    icon: ReactNode
    title: string
    message: string
    primaryLabel: string
    primaryIcon: ReactNode
    failureHints: FailureHints
}

type DeniedDevice = ProctoringBlockerType.Webcam | ProctoringBlockerType.Microphone

export const getProctoringBlockerContent = (
    blocker: ProctoringBlockerType,
    deniedDevices?: DeniedDevice[] | null
): ProctoringBlockerContent => {
    const effectiveBlocker = deniedDevices?.length === 1 ? deniedDevices[0] : blocker

    switch (effectiveBlocker) {
        case ProctoringBlockerType.MultiMonitorDetected:
            return {
                icon: <DesktopDisconnectedIcon />,
                title: 'Multiple Displays Connected',
                message:
                    'Looks like more than one display is connected. Please disconnect any additional displays to continue your task.',
                primaryLabel: CHECK_AGAIN_LABEL,
                primaryIcon: CHECK_AGAIN_ICON,
                failureHints: {},
            }
        case ProctoringBlockerType.Microphone:
            return {
                icon: <MicrophoneDisconnectedIcon />,
                title: 'Check Your Microphone',
                message:
                    'Looks like your microphone is disconnected. Please check and reconnect it to continue your task.',
                primaryLabel: CHECK_AGAIN_LABEL,
                primaryIcon: CHECK_AGAIN_ICON,
                failureHints: DEVICE_FAILURE_HINTS,
            }
        case ProctoringBlockerType.Webcam:
            return {
                icon: <WebcamDisconnectedIcon />,
                title: 'Check Your Webcam',
                message:
                    'Looks like your webcam is disconnected. Please check and reconnect it to continue your task.',
                primaryLabel: CHECK_AGAIN_LABEL,
                primaryIcon: CHECK_AGAIN_ICON,
                failureHints: DEVICE_FAILURE_HINTS,
            }
        case ProctoringBlockerType.ScreenShare:
            return {
                icon: <DesktopDisconnectedIcon />,
                title: 'Screen Share Needed',
                message:
                    'Looks like screen sharing has stopped. Please share your screen again to continue your task.',
                primaryLabel: 'Screen Share',
                primaryIcon: SCREEN_SHARE_ICON,
                failureHints: SCREEN_SHARE_FAILURE_HINTS,
            }
        default:
            return {
                icon: <DesktopDisconnectedIcon />,
                title: 'Action Needed',
                message: 'Something is blocking your task. Please check your setup and try again.',
                primaryLabel: CHECK_AGAIN_LABEL,
                primaryIcon: CHECK_AGAIN_ICON,
                failureHints: {},
            }
    }
}
